import { EntryShareButton } from "@/components/PredictionMarket/EntryShareButton";
import { MultipleChoicesGraph } from "@/components/PredictionMarket/MarketCardList/MultipleChoicesGraph";
import { Title } from "@/components/PredictionMarket/MarketCardList/Title";
import { PredictionChart } from "@/components/PredictionMarket/PredictionChart";
import { ChartList } from "@/components/PredictionMarket/PredictionChart/ChartList";
import { Tag } from "@/components/PredictionMarket/Tag";
import { DeadlineTime } from "@/components/homepage/RoundCard/components/DeadlineTime";
import { Skeleton } from "@/components/ui/skeleton";
import { useHandleError } from "@/hooks/useHandleError";
import { useNativeAppRedirect } from "@/hooks/useNativeAppRedirect";
import { isDemoMode } from "@/utils/demoUser";
import { ClockIcon, GroupUserIcon, ThumbaCoin } from "@roman/shared/components/Icons";
import { useMyGetPredictionMarketRanking } from "@roman/shared/hooks/PredictionMarket/useGetMyPredictionMarketRanking";
import { useGetPredictionMarketDetails } from "@roman/shared/hooks/PredictionMarket/useGetPredictionMarketDetails";
import { useGetPredictionMarketRanking } from "@roman/shared/hooks/PredictionMarket/useGetPredictionMarketRanking";
import { useAuthStore } from "@roman/shared/store/auth.store";
import type {
  MarketRankEnded,
  MarketRankLive,
  PredictionMarketChoiceWithProbability,
  PredictionMarketHistory,
  PredictionMarketHistoryChoiceSnapshot,
  PredictionMarketSnapshot,
} from "@roman/shared/types/PredictionMarket";
import { cn } from "@roman/shared/utils";
import { formatNumberWithCommas } from "@roman/shared/utils/formatNumberWithCommas";
import { generateDemoMarketDetails } from "@roman/shared/utils/predictionMarket/generateDemoMarkets";
import {
  PredictionMarketType,
  extractChoices,
  getPredictionMarketType,
  normalizeChoicesPercentage,
} from "@roman/shared/utils/predictionMarket/utils";
import type { Choice } from "@roman/shared/utils/predictionMarket/utils";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChoiceRanking } from "./components/ChoiceRanking";
import { CommentSection } from "./components/CommentSection";
import RelatedPredictionMarket from "./components/RelatedPredictionMarket";
import Description from "./components/Rules";
import { SelectChoice } from "./components/SelectChoice";
import { YesNoTabs } from "./components/YesNoTab";

// Utility function to convert v2 format to old format
// TODO: Update the chart component to support v2 format directly
const convertV2ToOldFormat = (
  historyV2: PredictionMarketHistory,
  choices: Array<{ id: string; name: string; probability: number; commentCount?: number }>,
): PredictionMarketSnapshot[] => {
  const { timestamps } = historyV2;
  const snapshots: PredictionMarketSnapshot[] = [];

  timestamps.forEach((timestamp, index) => {
    const predictionChoiceStates = choices.map((choice) => {
      const choiceId = choice.id;
      const probability = historyV2[choiceId]?.[index] ?? 0;

      return {
        choiceId,
        choiceName: choice.name,
        probability,
        commentCount: choice.commentCount ?? 0,
        totalFreePoints: "0", // Placeholder, as v2 does not have this data, and UI does not use it
        totalScore: "0", // Placeholder, as v2 does not have this data, and UI does not use it
      } satisfies PredictionMarketHistoryChoiceSnapshot;
    });

    snapshots.push({
      timeStamp: timestamp,
      predictionChoiceStates,
    });
  });

  return snapshots;
};

export const PredictionMarketDetails = () => {
  const isDemo = isDemoMode();
  const [activeTab, setActiveTab] = useState<"comments" | "ranking">("comments");
  const [participantChoiceRanks, setParticipantChoiceRanks] = useState<
    MarketRankLive["ranks"] | MarketRankEnded["ranks"]
  >([]);
  const [selectedChoice, setSelectedChoice] = useState<PredictionMarketChoiceWithProbability>();

  const [marketType, setMarketType] = useState<PredictionMarketType>(PredictionMarketType.TwoChoice);
  const [latestSnapshot, setLatestSnapshot] = useState<PredictionMarketSnapshot | null>(null);

  const [sortedChoices, setSortedChoices] = useState<Choice[]>([]);
  const [selectedChoiceIds, setSelectedChoiceIds] = useState<string[]>([]);
  const [predictionMarketSnapshots, setPredictionMarketSnapshots] = useState<PredictionMarketSnapshot[]>([]);

  const [showDescription, setShowDescription] = useState(true);

  const [isChartLineVisible, setIsChartLineVisible] = useState(false);

  const { predictionMarketId } = useParams<{ predictionMarketId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const hasSetTimestamp = useRef(false);

  if (!predictionMarketId) {
    throw new Error("predictionMarketId is required");
  }

  // Generate demo data if in demo mode
  const demoData = useMemo(
    () => (isDemo && predictionMarketId.startsWith("demo-") ? generateDemoMarketDetails(predictionMarketId) : null),
    [isDemo, predictionMarketId],
  );

  // Redirect to native app if on mobile device
  useNativeAppRedirect({
    deepLinkPath: `home?category=prediction-market&id=${predictionMarketId}`,
    enabled: true,
  });

  // Always update timestamp for OGP cache busting on page access
  useEffect(() => {
    if (hasSetTimestamp.current) return;

    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set("tid", Date.now().toString());
    const newUrl = `${location.pathname}?${newSearchParams.toString()}${location.hash}`;
    navigate(newUrl, { replace: true });
    hasSetTimestamp.current = true;
  }, [location.pathname, location.search, location.hash, navigate]);

  const predictionMarketRankingHandleError = useHandleError("予測市場ランキングの詳細を取得できませんでした");
  const authState = useAuthStore((state) => state.authState);

  // Use demo data if in demo mode, otherwise fetch from API
  // In demo mode, we skip API calls entirely by using the demo data
  const shouldUseMyRanking = !isDemo && authState === "authenticated";
  const shouldUsePublicRanking = !isDemo && authState !== "authenticated";

  const myRankingResult = shouldUseMyRanking
    ? useMyGetPredictionMarketRanking({
        predictionMarketId,
        swrOptions: {
          onError: predictionMarketRankingHandleError,
        },
      })
    : { data: undefined, isLoading: false, error: undefined };

  const publicRankingResult = shouldUsePublicRanking
    ? useGetPredictionMarketRanking({
        predictionMarketId,
        swrOptions: {
          onError: predictionMarketRankingHandleError,
        },
      })
    : { data: undefined, isLoading: false, error: undefined };

  const {
    data: apiPredictionMarketHistory,
    error: errorPredictionMarketHistory,
    isLoading: isLoadingPredictionMarketHistory,
  } = !isDemo
    ? useGetPredictionMarketDetails({
        predictionMarketId,
      })
    : { data: undefined, error: undefined, isLoading: false };

  // Select the appropriate ranking data
  const apiPredictionMarketRankings = shouldUseMyRanking ? myRankingResult.data : publicRankingResult.data;
  const isPredictionMarketRankingLoading = shouldUseMyRanking
    ? myRankingResult.isLoading
    : publicRankingResult.isLoading;

  // Use demo data if in demo mode, otherwise use API data
  const predictionMarketRankings = isDemo ? demoData?.predictionMarketRanking : apiPredictionMarketRankings;
  const predictionMarketHistory = isDemo ? demoData?.predictionMarketHistory : apiPredictionMarketHistory;

  const commentCountChoices = useMemo(() => {
    return predictionMarketHistory?.predictionMarket.predictionMarketChoices;
  }, [predictionMarketHistory]);

  useEffect(() => {
    if (!predictionMarketRankings?.marketRanks) return;

    if (predictionMarketHistory) {
      const choices = predictionMarketHistory.predictionMarket.predictionMarketChoices.map((choice) => ({
        id: choice.id,
        name: choice.name,
        commentCount: choice.commentCount,
      }));

      // Convert v2 format to old format for compatibility
      const oldFormatHistory = convertV2ToOldFormat(
        predictionMarketHistory.predictionMarketHistory,
        predictionMarketHistory.predictionMarket.predictionMarketChoices,
      );

      const sortedChoices = extractChoices(oldFormatHistory).sortedChoices;
      setSortedChoices(sortedChoices);
      if (marketType === PredictionMarketType.TwoChoice) {
        setSelectedChoiceIds([sortedChoices[0].id]);
      } else {
        setSelectedChoiceIds(sortedChoices.map((choice) => choice.id).slice(0, 4));
      }

      setMarketType(getPredictionMarketType(choices));
      setLatestSnapshot(oldFormatHistory[oldFormatHistory.length - 1]);

      setPredictionMarketSnapshots(oldFormatHistory);
    }
  }, [predictionMarketRankings, predictionMarketHistory, marketType]);

  useEffect(() => {
    if (!predictionMarketRankings?.marketRanks) return;
    if (!selectedChoice) return;
    const marketRanks = predictionMarketRankings.marketRanks;

    const matched = (marketRanks as MarketRankLive[]).find(
      (item) => item.predictionMarketChoiceId === selectedChoice.id,
    );
    const ranks = matched ? [...matched.ranks].sort((a, b) => a.rank - b.rank) : [];

    setParticipantChoiceRanks(ranks);
  }, [predictionMarketRankings, selectedChoice]);

  // Execute showData when LoadingSkeleton disappears
  useEffect(() => {
    if (
      !isPredictionMarketRankingLoading &&
      !isLoadingPredictionMarketHistory &&
      sortedChoices.length > 0 &&
      authState !== "fetchingUser"
    ) {
      setTimeout(() => {
        setIsChartLineVisible(true);
      }, 500);
    }
  }, [isPredictionMarketRankingLoading, isLoadingPredictionMarketHistory, sortedChoices.length, authState]);

  const scrollToCommentsAndRanking = useCallback(() => {
    const element = document.getElementById("comments-and-ranking");
    if (element) {
      const elementTop = element.offsetTop;
      const duration = 400; // Duration in milliseconds (smaller value = faster)
      const start = window.pageYOffset;
      const distance = elementTop - start;
      const startTime = performance.now();

      const animateScroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // easeOutCubic easing function
        const easeProgress = 1 - (1 - progress) ** 3;

        window.scrollTo(0, start + distance * easeProgress);

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    }
  }, []);

  useLayoutEffect(() => {
    if (location.hash === "#comments-and-ranking" && predictionMarketRankings && predictionMarketHistory) {
      setTimeout(() => {
        scrollToCommentsAndRanking();
      }, 1000);
    }
  }, [location.hash, predictionMarketRankings, predictionMarketHistory, scrollToCommentsAndRanking]);

  // Prepare defaultShowChartsIds based on market type
  // Must be before any conditional returns to comply with hooks rules
  const defaultShowChartsIds = useMemo(() => {
    if (sortedChoices.length === 0) return [];

    if (marketType === PredictionMarketType.TwoChoice) {
      return [sortedChoices[0].id];
    }
    // For MultiChoice, show top 4 choices
    return sortedChoices.map((choice) => choice.id).slice(0, 4);
  }, [sortedChoices, marketType]);

  if (
    !isDemo &&
    (isPredictionMarketRankingLoading || isLoadingPredictionMarketHistory || authState === "fetchingUser")
  ) {
    return <LoadingSkeleton />;
  }

  if (!predictionMarketRankings || !predictionMarketHistory) {
    if (errorPredictionMarketHistory) {
      return <div className="w-full h-full flex items-center justify-center">Error loading data</div>;
    }
    return <div className="w-full h-full flex items-center justify-center">No data available</div>;
  }

  const totalPoints = predictionMarketHistory.totalEnteredFreePoints;

  const isLive = predictionMarketRankings.status === "live";
  const isPrizeDistributed = predictionMarketRankings.status === "ended";

  const isEntryStillOpen = predictionMarketHistory.predictionMarket.entryCloseAt > Date.now();
  const choices = normalizeChoicesPercentage(latestSnapshot?.predictionChoiceStates || []);

  return (
    <div className="w-full h-full bg-white relative">
      <div className="h-10 w-full flex justify-between items-center py-[9px] px-4">
        <div className="flex items-center">
          <Tag text={predictionMarketHistory.predictionMarket.subLabel} />
          <div className="flex items-center ml-2">
            <ClockIcon className="text-dark-gray" />
            <DeadlineTime
              entryCloseTime={new Date(predictionMarketHistory.predictionMarket.entryCloseAt).getTime()}
              className="text-dark-gray font-medium text-xs"
            />
            {isPrizeDistributed && <span className="ml-1 text-dark-gray font-medium text-xs">終了</span>}
          </div>
        </div>

        <div className="flex justify-between items-center" onClick={(e) => e.stopPropagation()}>
          <EntryShareButton market={predictionMarketHistory.predictionMarket} className="text-dark-gray w-5 h-5" />
        </div>
      </div>

      <div className="pt-[6px] px-5 gap-5 flex flex-col">
        <div className="flex items-center justify-between gap-[10px]">
          {predictionMarketHistory.predictionMarket.primaryImageURL && (
            <img
              src={predictionMarketHistory.predictionMarket.primaryImageURL}
              alt={predictionMarketHistory.predictionMarket.title}
              className="object-cover rounded-lg h-[46px] w-[46px]"
            />
          )}
          <Title
            text={predictionMarketHistory.predictionMarket.title}
            className="font-extrabold text-lg text-black w-full line-clamp-2"
          />
        </div>
        <div className="w-full" key={`${predictionMarketHistory.predictionMarket.id}`}>
          {latestSnapshot && (
            <MultipleChoicesGraph
              selectedChoiceIds={selectedChoiceIds}
              choices={choices.map((choice) => ({
                id: choice.choiceId,
                name: choice.choiceName,
                probability: choice.probability,
                commentCount: commentCountChoices?.find((c) => c.id === choice.choiceId)?.commentCount ?? 0,
              }))}
              showAll
            />
          )}
          {isLive ? (
            <PredictionChart
              predictionMarketSnapshots={predictionMarketSnapshots}
              defaultShowChartsIds={defaultShowChartsIds}
              setChoiceIds={setSelectedChoiceIds}
              endTime={isEntryStillOpen ? null : predictionMarketHistory.predictionMarket.entryCloseAt}
              isLineVisible={isChartLineVisible}
            />
          ) : (
            <>
              <div className="mb-7">
                <PredictionChart
                  predictionMarketSnapshots={predictionMarketSnapshots}
                  defaultShowChartsIds={defaultShowChartsIds}
                  setChoiceIds={setSelectedChoiceIds}
                  endTime={predictionMarketHistory.predictionMarket.realEndTime}
                  isLineVisible={isChartLineVisible}
                />
              </div>
              <ChartList
                choices={sortedChoices}
                predictionMarketSnapshots={predictionMarketSnapshots}
                winningChoiceId={predictionMarketRankings.winningChoice.id}
                endTime={predictionMarketHistory.predictionMarket.realEndTime}
              />
            </>
          )}
        </div>

        {/* MONEY, NUM ENTRIES */}
        <div className="h-9 flex justify-between items-center">
          <div className="flex items-center h-[38px] w-full">
            <div className="w-[137px] flex flex-col h-[38px] relative">
              <div className="flex items-center h-[16px]">
                <ThumbaCoin className="w-[16px] h-[16px]" />
                <span className="text-[12px] text-dark-gray ml-[4px]">投票vol</span>
              </div>
              <div className="flex justify-end items-center font-roboto text-black font-bold absolute bottom-0 right-0 h-[18px]">
                <span className="text-[18px] mb-[2px]">{formatNumberWithCommas(totalPoints)}</span>
              </div>
            </div>

            <div className="ml-[39px] h-[38px] relative w-[64px]">
              <div className="">
                <div className="flex items-center">
                  <GroupUserIcon />
                  <span className="text-dark-gray text-[12px] ml-[5.64px] tracking-[-0.06em]">投票数</span>
                </div>
              </div>
              <span className="text-black text-[16px] font-bold absolute bottom-0 right-0">
                {formatNumberWithCommas(predictionMarketHistory.numberOfParticipants)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-5">
          <Description
            showDescription={showDescription}
            setShowDescription={setShowDescription}
            description={predictionMarketHistory.predictionMarket.description}
          />
        </div>
      </div>

      {!isDemo && (
        <div className="mt-[30px] px-5 pt-[6px] pb-[24.49px] w-full bg-light-blue">
          <RelatedPredictionMarket
            label={predictionMarketHistory.predictionMarket.label}
            id={predictionMarketHistory.predictionMarket.id}
          />
        </div>
      )}

      {/* tab section */}
      <div
        id="comments-and-ranking"
        className="mt-[30px] px-5 w-full"
        style={{
          minHeight: "calc(100vh - 150px)",
        }}
      >
        {/* top sticky */}
        <div className="flex h-[50px] bg-white sticky top-[72px] z-10">
          <div className="flex-1 flex flex-col items-center">
            <button
              type="button"
              className={`flex-1 text-center text-[16px] font-bold w-full transition-colors duration-500 ${
                activeTab === "comments" ? "text-black" : "text-light-gray"
              }`}
              onClick={() => setActiveTab("comments")}
            >
              コメント
            </button>
          </div>
          <div className="flex-1 flex flex-col items-center">
            <button
              type="button"
              className={`flex-1 text-center text-[16px] font-bold w-full transition-colors duration-500 ${
                activeTab === "ranking" ? "text-black" : "text-light-gray"
              }`}
              onClick={() => setActiveTab("ranking")}
            >
              ランキング
            </button>
          </div>
          <div className="absolute bottom-0 h-[2px] bg-light-gray w-full" />
          <div
            className={`absolute bottom-0 h-[2px] w-1/2 bg-[#4B57FF] transition-transform duration-500 ease-in-out  ${
              activeTab === "comments" ? "translate-x-0" : "translate-x-full"
            }`}
          />
        </div>

        {activeTab === "comments" && (
          <div className="">
            {isDemo ? (
              <div className="flex flex-col items-center justify-center mt-20">
                <p className="text-dark-gray text-center">デモモードではコメント機能は利用できません</p>
              </div>
            ) : (
              <CommentSection
                predictionMarketId={predictionMarketHistory.predictionMarket.id}
                choices={sortedChoices}
              />
            )}
          </div>
        )}

        {activeTab === "ranking" && (
          <>
            <div
              className={cn(
                "sticky top-[122px] pt-[20px] pb-1",
                participantChoiceRanks.length === 0 ? "bg-transparent" : "bg-white",
              )}
            >
              {predictionMarketHistory.numberOfParticipants === 0 ? (
                <div className="flex flex-col items-center justify-center">
                  <h3 className="text-black font-bold text-[20px] leading-[1.45] mt-[197px]">
                    ランキングはまだありません
                  </h3>
                  <p className="text-dark-gray font-normal text-[13px] leading-[1.45] mt-1">
                    TOP15位のユーザーが表示されます
                  </p>
                </div>
              ) : (
                <div className="pb-1">
                  {
                    // isLive &&
                    marketType === PredictionMarketType.TwoChoice ? (
                      <YesNoTabs
                        choices={sortedChoices}
                        selectedChoice={selectedChoice}
                        setSelectedChoice={(choice) => {
                          const choiceWithProb = sortedChoices.find((sc) => sc.id === choice.id);
                          if (choiceWithProb) {
                            setSelectedChoice({
                              id: choiceWithProb.id,
                              name: choiceWithProb.name,
                              probability: choiceWithProb.latestProbability || 0,
                            });
                          }
                        }}
                      />
                    ) : (
                      <SelectChoice
                        selectedChoice={selectedChoice}
                        listChoices={sortedChoices}
                        setSelectedChoice={(choice) => {
                          const choiceWithProb = sortedChoices.find((sc) => sc.id === choice.id);
                          if (choiceWithProb) {
                            setSelectedChoice({
                              id: choiceWithProb.id,
                              name: choiceWithProb.name,
                              probability: choiceWithProb.latestProbability || 0,
                            });
                          }
                        }}
                      />
                    )
                  }
                </div>
              )}
              <div
                className="absolute bottom-[-20px] left-0 right-0 h-[20px] pointer-events-none"
                style={{
                  background: "linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))",
                }}
              />
            </div>

            <div className="pb-[100px]">
              {participantChoiceRanks.length > 0 ? (
                <>
                  <ChoiceRanking isEnd={isPrizeDistributed} participantChoiceRanks={participantChoiceRanks} />
                </>
              ) : isPredictionMarketRankingLoading ? (
                <LoadingSkeleton />
              ) : (
                predictionMarketHistory.numberOfParticipants !== 0 && (
                  <div className="flex flex-col items-center justify-center">
                    <h3 className="text-black font-bold text-[20px] leading-[1.45] mt-[80px]">
                      ランキングはまだありません
                    </h3>
                    <p className="text-dark-gray font-normal text-[13px] leading-[1.45] mt-1">
                      TOP15位のユーザーが表示されます
                    </p>
                  </div>
                )
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const LoadingSkeleton = () => {
  return (
    <div className="bg-white">
      <Skeleton className="w-full h-[120px]" />

      <div className="px-4">
        <div className="flex justify-between py-2">
          <div className="flex gap-2">
            <Skeleton className="w-20 h-6" />
            <Skeleton className="w-10 h-6" />
          </div>
          <Skeleton className="h-6 w-6 " />
        </div>

        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-[300px] w-full mt-3" />
        <Skeleton className="h-12 w-full mt-2" />
        <Skeleton className="h-10 w-1/3 mt-2" />

        <Skeleton className="h-10 w-full mt-4 hidden md:block" />
        <Skeleton className="h-10 w-full mt-4 hidden md:block" />

        <div className="flex gap-2 mt-3 p-5">
          <Skeleton className="h-14 w-1/2" />
          <Skeleton className="h-14 w-1/2" />
        </div>
      </div>
    </div>
  );
};
